import { Box, styled } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import DVHLineChart from '~/components/Charts/DVHLineChart';
import SwapToFullScreen from '~/containers/viewer/viewport/ViewportOverlay/SwapToFullScreen';
import { CurveData } from '~/domain/patient/dvh';
import useDose from '~/hooks/patient/dose/useDose';
import useDvh from '~/hooks/patient/dose/useDvh';
import { roisForDvhState } from '~/state/atoms/structureSet/roiAtoms';
import { showDVHViewComponentFullScreenState } from '~/state/atoms/viewerAtoms';

const ChartOverlayWrap = styled(Box)`
    width: 100%;
    height: 100%;
    position: relative;
    border: 1px solid ${({ theme }) => theme.palette.divider};
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
`;

const ChartOverlaySectionWrap = styled(Box)`
    position: absolute;
`;

const DVHLineChartContainer = styled(Box)`
    height: 95%;
    width: 99%;
`;

const DVHLineChartComponent = () => {
    const { dose } = useDose();
    const { getDvhForRoi } = useDvh();
    const roisForDvh = useRecoilValue(roisForDvhState);
    const [showDVHViewComponentFullScreen, setShowDVHViewComponentFullScreen] =
        useRecoilState(showDVHViewComponentFullScreenState);

    const containerRef = useRef<HTMLDivElement>(null);
    const [dvhData, setDvhData] = useState<CurveData[]>([]);
    const curvesRef = useRef<CurveData[]>([]);

    const [isComputing, setIsComputing] = useState(false);

    useEffect(() => {
        let cancelled = false;
        curvesRef.current = [];
        setDvhData([]);

        if (!dose || !roisForDvh.length) {
            return;
        }
        setIsComputing(true);

        const computeOne = async (
            roiNumber: number
        ): Promise<CurveData | undefined> => {
            // Yield to let UI paint between items
            await new Promise((r) => requestAnimationFrame(r));
            if (cancelled) {
                return;
            }
            return getDvhForRoi(roiNumber);
        };

        (async () => {
            const tasks = roisForDvh.map(async (roi) => {
                const curve = await computeOne(roi.roiNumber);
                if (cancelled || !curve) {
                    return;
                }
                curvesRef.current.push(curve);
                setDvhData([...curvesRef.current]);
            });
            await Promise.all(tasks);
            if (!cancelled) {
                setIsComputing(false);
            }
        })();

        return () => {
            cancelled = true;
            setIsComputing(false);
        };
    }, [dose, roisForDvh, getDvhForRoi]);

    return (
        <ChartOverlayWrap>
            <ChartOverlaySectionWrap right={15} top={15} sx={{ zIndex: 999 }}>
                <SwapToFullScreen
                    showFullscreen={showDVHViewComponentFullScreen}
                    setShowFullscreen={setShowDVHViewComponentFullScreen}
                />
            </ChartOverlaySectionWrap>
            {dose && (
                <DVHLineChartContainer
                    ref={containerRef}
                    data-testid="dvh-line-chart-container"
                >
                    <DVHLineChart
                        key={
                            showDVHViewComponentFullScreen
                                ? 'fullscreen'
                                : 'normal'
                        }
                        data={dvhData}
                        normalizationValueGy={dose.normalizationValueGy}
                        loading={isComputing}
                        expanded={showDVHViewComponentFullScreen}
                    />
                </DVHLineChartContainer>
            )}
        </ChartOverlayWrap>
    );
};

export default DVHLineChartComponent;
